import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { logger } from 'src/utils/logger';
import { refreshAccessToken } from 'src/utils/axios';
import { signOutJsrSession } from 'src/utils/jsr-auth';

import { varAlpha } from 'src/theme/styles';
import useAppLoading from 'src/stores/loading';
import useUserSessionStore from 'src/stores/user-session';
import oneviewLogo from 'src/theme/themes/oneview/logo.png';
import infosyncLogo from 'src/theme/themes/infosync/logo.png';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateAvatar } from 'src/components/animate';
import { SystemSettings } from 'src/components/system-settings';

import { useAuthContext } from 'src/auth/hooks';
import { signOutAll } from 'src/auth/context/jwt';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';

// ----------------------------------------------------------------------

export function AccountDrawer({ data = [], sx, ...other }) {
  const theme = useTheme();

  const router = useRouter();

  const { startAppLoading, stopAppLoading } = useAppLoading();
  const { sessions, switchSession, logoutAll, activeSessionId } = useUserSessionStore((state) => state);
  const { user, checkUserSession } = useAuthContext();

  const getThemeLogo = useCallback((themeName) => {
    if (!themeName) return null;
    const normalized = String(themeName).toLowerCase();
    if (normalized === 'infosync') return infosyncLogo;
    if (normalized === 'oneview') return oneviewLogo;
    return null;
  }, []);

  const sessionItems = sessions.map((session) => {
    const payload = session.user || {};
    const tenantLabel = payload.clientName || payload.tenantId || payload.tenant_id || 'Unknown Tenant';
    const username = payload.username || 'Unknown User';
    const themeLogo = getThemeLogo(payload.theme);

    return {
      sessionId: session.sessionId,
      isActive: session.sessionId === activeSessionId,
      avatarSrc: themeLogo || payload.logo || payload.photoURL,
      avatarAlt: username,
      primaryText: tenantLabel,
      secondaryText: username,
    };
  }).sort((a, b) => Number(b.isActive) - Number(a.isActive));

  const [open, setOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogoutAll = useCallback(async () => {
    try {
      await signOutAll();
      await checkUserSession?.();
      router.refresh();
    } catch (error) {
      logger.error('Error during logout all sessions from drawer:', error);
    }
  }, [checkUserSession, router]);

  const handleSwitchSession = useCallback(
    async (sessionId) => {
      startAppLoading();

      await signOutJsrSession({ serverUrl: user?.jsrIntegration?.serverUrl });

      // Lazy refresh: if we are switching to a session and for some reason we don't have the token in memory
      // (e.g. it wasn't the active one on load), we might want to ensure we have it.
      // However, usually just switching the activeSessionId and letting the AuthProvider/Axios handle the next 401 is fine,
      // OR we can explicitly refresh here to be safe and avoid a flashing error.

      switchSession(sessionId);

      // After switching ID, try to refresh to ensure we have a valid access token for this session
      // This works because refreshAccessToken uses the *active* session ID from the store
      try {
        // We only need to do this if we don't already have one?
        // Actually, just firing a refresh is safer to ensure it's valid.
        await refreshAccessToken();
      } catch (e) {
        logger.warn("Failed to refresh token for target session on switch", e);
      }

      handleCloseDrawer();

      // Trigger a reload or re-fetch to ensure the app state updates with the new session
      setTimeout(() => {
        window.location.reload();
        // Or simply route and let checkUserSession run?
        // Reload is safer to clear previous user state completely from memory.
        stopAppLoading();
      }, 500);
    },
    [handleCloseDrawer, switchSession, startAppLoading, stopAppLoading, user?.jsrIntegration?.serverUrl]
  );

  const handleGoTo = useCallback(
    (href) => {
      handleCloseDrawer();
      router.push(href);
    },
    [handleCloseDrawer, router]
  );

  const handleAddAccount = useCallback(
    () => {
      handleCloseDrawer();
      // We generally want to go to the login page again, but we need to ensure we don't just "resume" the current session.
      // However, usually "Add Account" implies going to login.
      // If we go to login while logged in, the auth guard might redirect us back.
      // We might need a specific param like ?add=true and handle it in the login page layout.
      router.push(paths.account.addAccount);
    },
    [handleCloseDrawer, router]
  );

  const handleOpenSettings = useCallback(() => {
    handleCloseDrawer();
    setOpenSettings(true);
  }, [handleCloseDrawer]);

  const handleCloseSettings = useCallback(() => {
    setOpenSettings(false);
  }, []);

  const renderAvatar = (
    <AnimateAvatar
      width={96}
      slotProps={{
        avatar: { src: user?.photoURL, alt: user?.firstName },
        overlay: {
          border: 2,
          spacing: 3,
          color: `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
        },
      }}
    >
      {user?.firstName?.charAt(0).toUpperCase()}
    </AnimateAvatar>
  );

  return (
    <>
      <AccountButton
        open={open}
        onClick={handleOpenDrawer}
        photoURL={user?.photoURL}
        displayName={user?.firstName}
        sx={sx}
        {...other}
        data-testid="account-button"
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true }, paper: { sx: { width: 320 } } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Stack alignItems="center" sx={{ pt: 8 }}>
            {renderAvatar}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }} data-testid="account-drawer-user-name">
              {`${user?.firstName} ${user?.lastName}`}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap data-testid="account-drawer-user-email">
              {user?.email}
            </Typography>
          </Stack>

          <Box sx={{ px: 2.5, pt: 3, mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Switch account
            </Typography>
          </Box>

          <MenuList sx={{ px: 1.5, py: 1, maxHeight: 168, overflowY: 'auto' }}>
            {sessionItems.map((session) => (
              <MenuItem
                key={session.sessionId}
                selected={session.isActive}
                data-testid={`account-session-${session.secondaryText}`}
                onClick={() => {
                  if (!session.isActive) {
                    handleSwitchSession(session.sessionId);
                  }
                }}
                sx={{
                  py: 1,
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: varAlpha(theme.vars.palette.primary.mainChannel, 0.14),
                    color: 'text.primary',
                    '& .MuiListItemText-secondary': {
                      color: 'text.primary',
                    },
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: varAlpha(theme.vars.palette.primary.mainChannel, 0.18),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    alt={session.avatarAlt}
                    src={session.avatarSrc}
                    data-testid={`account-session-avatar-${session.secondaryText}`}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={session.primaryText}
                  secondary={session.secondaryText}
                  primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', noWrap: true }}
                />

                {session.isActive && (
                  <Label color="success" sx={{ ml: 1 }}>
                    Current
                  </Label>
                )}
              </MenuItem>
            ))}
          </MenuList>

          <MenuItem
            onClick={handleAddAccount}
            data-testid="account-add-account"
            sx={{
              py: 1,
              mx: 2.5,
              mt: 1,
              borderRadius: 1,
              color: 'text.secondary',
              '& svg': { width: 20, height: 20 },
              '&:hover': { color: 'text.primary' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Iconify icon="mingcute:add-line" />
            </ListItemIcon>
            <ListItemText
              primary="Add account"
              primaryTypographyProps={{ variant: 'subtitle2' }}
            />
          </MenuItem>

          <Stack
            sx={{
              py: 3,
              px: 2.5,
              borderTop: `dashed 1px ${theme.vars.palette.divider}`,
              // borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
            }}
          >

            {data.map((option) => {
              return (
                <MenuItem
                  key={option.label}
                  onClick={() => handleGoTo(option.href)}
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& svg': { width: 24, height: 24 },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {option.icon}

                  <Box component="span" sx={{ ml: 2 }}>
                    {option.label}
                  </Box>

                  {option.info && (
                    <Label color="error" sx={{ ml: 1 }}>
                      {option.info}
                    </Label>
                  )}
                </MenuItem>
              );
            })}

            <MenuItem
              onClick={handleOpenSettings}
              sx={{
                py: 1,
                color: 'text.secondary',
                '& svg': { width: 24, height: 24 },
                '&:hover': { color: 'text.primary' },
              }}
            >
              <Iconify icon="solar:settings-bold-duotone" />
              <Box component="span" sx={{ ml: 2 }}>
                Settings
              </Box>
            </MenuItem>

          </Stack>
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={handleCloseDrawer} data-testid="account-logout" />
          <Button
            fullWidth
            sx={{ mt: 1.5 }}
            variant="soft"
            size="large"
            color="error"
            onClick={handleLogoutAll}
            data-testid="account-logout-all"
          >
            Logout All
          </Button>
        </Box>
      </Drawer>

      <SystemSettings open={openSettings} onClose={handleCloseSettings} />
    </>
  );
}
